import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { AppThunk, RootState } from "store"
import { GetLocationQRCodeInterface } from "@type/locations.types"
import LocationQRCodeService from "services/locations/locationQRCode.service"
import { updateState } from "config/utils"
import { locationQRCode } from "services"

interface LocationQRCodesState {
	list: GetLocationQRCodeInterface[]
	loading: boolean
	saving: boolean
}

const initialState: LocationQRCodesState = {
	list: [],
	saving: false,
	loading: false,
}

const locationQRCodesSlice = createSlice({
	name: "locationQRCodes",
	initialState,
	reducers: {
		fetchingQRCodes: state => {
			state.loading = true
		},
		qrCodesFetched: (state, action: PayloadAction<GetLocationQRCodeInterface[]>) => {
			state.list = action.payload
			state.loading = false
		},
		qrCodesFailed: state => {
			state.loading = false
		},
		savingQrCode: state => {
			state.saving = true
		},
		qrCodeSaved: (state, action: PayloadAction<GetLocationQRCodeInterface | GetLocationQRCodeInterface[]>) => {
			state.saving = false
			const [updatedList] = updateState(state.list, action.payload, "_id")
			state.list = updatedList
		},
		qrCodeSavingFailed: state => {
			state.saving = false
		},
		softDeleteQrCode: (state, action: PayloadAction<GetLocationQRCodeInterface[]>) => {
			state.saving = false
			state.list = action.payload
		},
	},
})

export default locationQRCodesSlice.reducer

export const {
	fetchingQRCodes,
	qrCodesFetched,
	qrCodesFailed,
	savingQrCode,
	qrCodeSaved,
	qrCodeSavingFailed,
	softDeleteQrCode,
} = locationQRCodesSlice.actions

const getQRCodesByLocationId =
	(locationId: string): AppThunk =>
	async dispatch => {
		dispatch(fetchingQRCodes())
		try {
			const { data } = await LocationQRCodeService.getLocationQRCodes(locationId)
			dispatch(qrCodesFetched(data))
		} catch (error) {
			dispatch(qrCodesFailed())
		}
	}

const saveLocationQRCode =
	(id: string, payload: Partial<GetLocationQRCodeInterface>): AppThunk =>
	async dispatch => {
		let locationQRCodeResponse: GetLocationQRCodeInterface | GetLocationQRCodeInterface[] | null = null
		try {
			dispatch(savingQrCode())

			if (payload._id) locationQRCodeResponse = (await locationQRCode.updateLocationQRCode(id, payload)).data
			else locationQRCodeResponse = (await locationQRCode.createLocationQRCode(id, payload)).data.data

			dispatch(qrCodeSaved(locationQRCodeResponse))
		} catch (error) {
			dispatch(qrCodeSavingFailed())
		}
	}

const deleteLocationQRCodeById =
	(id: string, qrId: string): AppThunk =>
	async dispatch => {
		try {
			dispatch(savingQrCode())
			const {
				data: { data: qualificationResponse },
			} = await locationQRCode.deleteLocationQRCode(id, qrId)
			dispatch(softDeleteQrCode(qualificationResponse))
		} catch (error) {
			dispatch(qrCodeSavingFailed())
		}
	}

export { getQRCodesByLocationId, saveLocationQRCode, deleteLocationQRCodeById }

const selectQRCodesByLocationId = (state: RootState) => state.location.qrCodes.list
const isQRCodesLoading = (state: RootState) => state.location.qrCodes.loading

export { selectQRCodesByLocationId, isQRCodesLoading }
